import { z } from 'zod';
import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { isWholeQuantity } from '../../utils/is-whole-quantity.js';
import { WHOLE_QUANTITY_MESSAGE } from '../quantity/whole-quantity-message.js';
import { SALES_ORDER_LINE_CONSTRAINTS } from './constraints.js';
import { salesOrderLineNoteSchema } from './item-note.js';
import { salesOrderLineQuantitySchema } from './item-quantity.js';
import { salesOrderLineUnitPriceSchema } from './item-unit-price.js';
import { ZERO_REVENUE_LINE_TYPES } from './zero-revenue-line-types.js';
export const SALES_ORDER_LINE_NEEDS_PRICE_MESSAGE = "This line needs a unit price, and your role can't set one.";
const lineTypeSchema = z.enum(SalesOrderItemType, `Invalid ${SALES_ORDER_LINE_CONSTRAINTS.lineType.label.toLowerCase()}.`);
/** One Edit Lines row: a saved line (`key` is its id) or a staged one (`key` is its client key). */
const rowShape = z.object({
    key: z.string().min(1),
    kind: z.enum(['existing', 'new']),
    productId: z.string().nullable(),
    productNum: z.string(),
    /** The product's name: a new line's until it is saved, an existing line's when it has no stored name. */
    productName: z.string().nullable(),
    /** The product's own category (never its ancestry); empty when it has none. */
    categoryId: z.string(),
    categoryName: z.string(),
    /** The saved line's type; null on a new line. */
    savedTypeId: z.number().nullable(),
    qty: z.number().or(z.nan()),
    /** The server's whole-quantity rule for the row's unit. */
    qtyIntegral: z.boolean(),
    uomId: z.string().nullable(),
    uomCode: z.string(),
    typeId: z.number(),
    /** Decimal text as typed; kept while the row is Sample/Promo, which always saves at $0. */
    unitPrice: z.string(),
    /** The stored name: shown and hashed into the fingerprint, never edited here. */
    name: z.string(),
    note: z.string(),
    removed: z.boolean(),
    /** Someone else deleted the line; shown until dismissed and never sent. */
    orphaned: z.boolean(),
});
const firstMessage = (result) => result.error?.issues[0]?.message;
function checkQuantity(row, issue) {
    if (!Number.isFinite(row.qty))
        return issue('qty', 'Enter a quantity.');
    const message = firstMessage(salesOrderLineQuantitySchema.safeParse(row.qty));
    if (message)
        return issue('qty', message);
    if (row.qtyIntegral && !isWholeQuantity(row.qty))
        issue('qty', WHOLE_QUANTITY_MESSAGE);
}
function needsPrice(row) {
    // The price is in question on a new line, or on a line leaving Sample/Promo.
    const priceRequired = row.kind === 'new' ||
        (row.savedTypeId !== null && ZERO_REVENUE_LINE_TYPES.has(row.savedTypeId));
    return priceRequired && (row.unitPrice.trim() === '' || Number(row.unitPrice) === 0);
}
function checkPrice(row, issue, { canOverridePrice }) {
    if (ZERO_REVENUE_LINE_TYPES.has(row.typeId))
        return;
    // A rep without price override can't fix a missing price, so a priced line never saves at $0.00.
    if (!canOverridePrice && needsPrice(row))
        return issue('unitPrice', SALES_ORDER_LINE_NEEDS_PRICE_MESSAGE);
    const price = Number(row.unitPrice);
    if (row.unitPrice.trim() === '' || !Number.isFinite(price)) {
        return issue('unitPrice', 'Enter a unit price.');
    }
    const message = firstMessage(salesOrderLineUnitPriceSchema.safeParse(price));
    if (message)
        issue('unitPrice', message);
}
/** One Edit Lines row; a removed or orphaned row is never sent, so its rules are skipped. */
export function salesOrderLineEditRowSchemaFor(options) {
    return rowShape.superRefine((row, context) => {
        if (row.removed || row.orphaned)
            return;
        const issue = (field, message) => context.addIssue({ code: 'custom', path: [field], message });
        checkQuantity(row, issue);
        const typeMessage = firstMessage(lineTypeSchema.safeParse(row.typeId));
        if (typeMessage)
            issue('typeId', typeMessage);
        checkPrice(row, issue, options);
        const noteMessage = firstMessage(salesOrderLineNoteSchema.safeParse(row.note));
        if (noteMessage)
            issue('note', noteMessage);
    });
}
/** The Edit Lines form: one row schema per line, under `rows`. */
export function salesOrderLineEditFormSchemaFor(options) {
    return z.object({ rows: z.array(salesOrderLineEditRowSchemaFor(options)) });
}
export const salesOrderLineEditRowSchema = salesOrderLineEditRowSchemaFor({
    canOverridePrice: true,
});
export const salesOrderLineEditFormSchema = salesOrderLineEditFormSchemaFor({
    canOverridePrice: true,
});
//# sourceMappingURL=line-edit-form.js.map